import {
  ChartDataPoint,
  CurtailmentData,
  HealthEvent,
  OutageSpan,
  ReadingsDataProcessedResponse,
  WeatherActualsData,
} from "@inrange/building-manager-api-client";
import ReactECharts from "echarts-for-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import {
  aggregateByTimePeriod,
  bucketCurtailment,
  buildBucketTsMap,
  buildRawTsToBarX,
  getBucketKey,
  inferSettlementPeriodMs,
} from "./chartAggregation";
import { AggregationMode, colors, Formatters, PresetName } from "./types";

const PRESETS: Record<
  PresetName,
  { series: string[]; batteryExtras?: string[] }
> = {
  default: {
    series: [
      "Generation",
      "Demand",
      "On-site direct from PV",
      "Export",
      "Health events",
    ],
    batteryExtras: [
      "On-site battery discharge from PV",
      "Battery Final Charge",
    ],
  },
  availability: {
    series: [
      "Generation",
      "PV Availability: Expected gen. period",
      "PV Availability: Unavailable gen. period",
      "Health events",
    ],
  },
  "performance-ratio": {
    series: [
      "Generation",
      "Expected Gen. (Meteonorm)",
      "GPR (Meteonorm)",
      "OPR (Meteonorm)",
      "PR: Curtailed period",
      "PR: Estimated curtailed generation",
      "Health events",
    ],
  },
  "gen-vs-forecast": {
    series: [
      "Generation",
      "Forecast Generation (P50)",
      "Forecast Generation (P90)",
    ],
  },
  "demand-vs-forecast": {
    series: ["Demand", "Forecast Demand"],
  },
};

const ChartProcessed = ({
  data,
  selectedEsysUuid,
  startDate,
  endDate,
  selectedSources,
  hasBattery,
  hasAnyZeroExport,
  isImportOnly,
  formatters,
  aggregationMode = "none",
  forecastP50Data,
  forecastP90Data,
  weatherActualsData,
  curtailmentData,
  expectedGenerationPeriods,
  eventLogEvents,
  esysEntries,
  siteName,
  preset = "default",
  onPresetChange,
  forecastLoading,
}: {
  data: ReadingsDataProcessedResponse;
  selectedEsysUuid: string | undefined;
  startDate: string;
  endDate: string;
  selectedSources: string[];
  hasBattery: boolean;
  hasAnyZeroExport: boolean;
  isImportOnly: boolean;
  formatters: Formatters;
  aggregationMode?: AggregationMode;
  forecastP50Data?: ChartDataPoint[];
  forecastP90Data?: ChartDataPoint[];
  weatherActualsData?: WeatherActualsData;
  curtailmentData?: CurtailmentData;
  expectedGenerationPeriods?: boolean[];
  eventLogEvents?: HealthEvent[];
  esysEntries?: [string, any][];
  siteName?: string;
  preset?: PresetName;
  onPresetChange?: (preset: PresetName) => void;
  forecastLoading?: boolean;
}) => {
  // Track user's legend selections by base name so they persist across re-renders
  const legendSelectionsRef = useRef<Record<string, boolean> | null>(null);

  const handleLegendChange = useCallback(
    (params: { selected: Record<string, boolean> }) => {
      const selections: Record<string, boolean> = {};
      Object.entries(params.selected).forEach(([name, isSelected]) => {
        const baseName = name.replace(/ \([^)]*\)$/, "");
        selections[baseName] = isSelected;
      });
      legendSelectionsRef.current = selections;
      setVisibleSeries(params.selected);
    },
    []
  );

  const handleCustomLegendClick = useCallback((name: string) => {
    const instance = chartRef.current?.getEchartsInstance();
    if (!instance) return;
    instance.dispatchAction({ type: "legendToggleSelect", name });
  }, []);

  const chartRef = useRef<ReactECharts | null>(null);
  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>(
    {}
  );

  const handlePresetClick = useCallback(
    (newPreset: PresetName) => {
      // Clear saved legend selections so the preset takes effect
      legendSelectionsRef.current = null;
      onPresetChange?.(newPreset);
    },
    [onPresetChange]
  );

  // Process chart data for processed readings
  const {
    chartOptions,
    filteredData,
    performanceRatio,
    availabilityPct,
    legendRows,
    legendSelected,
    legendColorMap,
  } = useMemo(() => {
    const allData = data.data;
    if (!allData || allData.length === 0)
      return {
        chartOptions: {},
        filteredData: [],
        availabilityPct: null as number | null,
        legendRows: [] as string[][],
        legendSelected: {} as Record<string, boolean>,
        legendColorMap: {} as Record<string, string>,
      };

    // Filter data by esysUuid and date range
    // Pre-calculate timestamp bounds for efficient filtering
    const { startMs, endMs } =
      startDate && endDate
        ? formatters.dateRangeToTimestamps(startDate, endDate)
        : { startMs: 0, endMs: Infinity };

    let filteredData = allData.filter((point) => {
      // Filter by esysUuid
      if (selectedEsysUuid && point.esysUuid !== selectedEsysUuid) return false;

      // Date range filter using pre-calculated timestamp bounds
      if (startDate && endDate) {
        const pointMs = new Date(point.x).getTime();
        if (pointMs < startMs || pointMs > endMs) return false;
      }
      return true;
    });

    // When a specific esys is selected, use its own expected generation series
    // so "Expected Gen. (Meteonorm)" is scoped to that esys. Fall back to
    // whole-site sum when no esys is selected or when the per-esys breakdown
    // is unavailable (older backend response).
    //
    // Two parallel series: `waInrangeSeries` is loss-factor-adjusted and feeds
    // the "Expected Gen. (Meteonorm)" chart line (a tighter envelope to spot
    // deviations). `waIdealSeries` is the contract-ideal denominator (no loss
    // factors, year-indexed degradation only) and feeds the PR ratio so the
    // chart's PR matches the contract-aligned PR on the site health page.
    const waInrangeSeries: number[] | undefined = weatherActualsData
      ? ((selectedEsysUuid &&
          weatherActualsData.fGenerationInrangeByEsys?.[selectedEsysUuid]) ||
          weatherActualsData.fGenerationInrange)
      : undefined;
    const waIdealSeries: number[] | undefined = weatherActualsData
      ? ((selectedEsysUuid &&
          weatherActualsData.fGenerationIdealByEsys?.[selectedEsysUuid]) ||
          weatherActualsData.fGenerationIdeal)
      : undefined;

    // If no specific esysUuid is selected (whole site), aggregate data by timestamp
    if (!selectedEsysUuid && filteredData.length > 0) {
      const aggregatedMap = new Map();

      filteredData.forEach((point) => {
        const key = point.x;
        if (!aggregatedMap.has(key)) {
          aggregatedMap.set(key, {
            x: point.x,
            esysUuid: "aggregated",
            demand: 0,
            generation: 0,
            behindMeterPV: 0,
            import: 0,
            export: 0,
            exportRaw: 0,
            behindMeterBattery: 0,
            batteryChargeFromPV: 0,
            batteryChargeFromGrid: 0,
            batteryDischargeGrid: 0,
            batteryAuxDemand: 0,
            pvAuxDemand: 0,
            batteryFinalCharge: 0,
            exportMetered: 0,
            networkImport: 0,
            generationSource: new Set<string>(),
            importSource: new Set<string>(),
            exportSource: new Set<string>(),
            batteryFinalChargeSource: new Set<string>(),
          });
        }

        const agg = aggregatedMap.get(key);
        agg.demand += point.demand;
        agg.generation += point.generation;
        agg.behindMeterPV += point.behindMeterPV;
        agg.import += point.import;
        agg.export += point.export;
        agg.exportRaw += point.exportRaw;
        agg.behindMeterBattery += point.behindMeterBattery;
        agg.batteryChargeFromPV += point.batteryChargeFromPV;
        agg.batteryChargeFromGrid += point.batteryChargeFromGrid;
        agg.batteryDischargeGrid += point.batteryDischargeGrid;
        agg.batteryAuxDemand += point.batteryAuxDemand;
        agg.pvAuxDemand += point.pvAuxDemand;
        agg.batteryFinalCharge += point.batteryFinalCharge;
        agg.exportMetered += point.exportMetered ?? 0;
        agg.networkImport += point.networkImport ?? 0;

        // Collect all unique sources
        if (point.generationSource)
          agg.generationSource.add(point.generationSource);
        if (point.importSource) agg.importSource.add(point.importSource);
        if (point.exportSource) agg.exportSource.add(point.exportSource);
        if (point.batteryFinalChargeSource)
          agg.batteryFinalChargeSource.add(point.batteryFinalChargeSource);
      });

      // Convert sets to comma-separated strings and create final array
      filteredData = Array.from(aggregatedMap.values())
        .map((agg) => ({
          ...agg,
          generationSource:
            agg.generationSource.size > 0
              ? Array.from(agg.generationSource).join(", ")
              : undefined,
          importSource:
            agg.importSource.size > 0
              ? Array.from(agg.importSource).join(", ")
              : undefined,
          exportSource:
            agg.exportSource.size > 0
              ? Array.from(agg.exportSource).join(", ")
              : undefined,
          batteryFinalChargeSource:
            agg.batteryFinalChargeSource.size > 0
              ? Array.from(agg.batteryFinalChargeSource).join(", ")
              : undefined,
        }))
        .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());
    }

    // Helper function to check if a source should be included
    const shouldIncludeSource = (source: string | undefined) => {
      if (selectedSources.length === 0) return true;
      return !!source && selectedSources.includes(source);
    };

    // Settlement-period resolution data, retained after bucketing. Everything
    // that reasons about individual settlement periods - the curtailment
    // overlay, the expected-generation mask, the PR numerator - reads this,
    // because `filteredData` below collapses to one point per bucket.
    const rawPoints = filteredData;

    // Apply time-period aggregation if requested
    if (aggregationMode !== "none") {
      filteredData = aggregateByTimePeriod(
        filteredData,
        aggregationMode,
        formatters,
        shouldIncludeSource
      );
    }

    if (filteredData.length === 0)
      return { chartOptions: {}, filteredData: [] };

    const isAggregated = aggregationMode !== "none";

    // Source filtering is applied inside aggregateByTimePeriod at raw
    // resolution, so aggregated values are already masked. Re-testing the
    // filter against a bucket's joined source string ("a, b") would never
    // match and would zero out every mixed-source bucket.
    const sourced = (
      value: number,
      source: string | undefined
    ): number | null => {
      if (isAggregated) {
        // An empty aggregated source means no raw period in the bucket passed
        // the filter, so the bucket has no reading to show - a gap, not a zero.
        return selectedSources.length > 0 && !source ? null : value;
      }
      return shouldIncludeSource(source) ? value : null;
    };
    const sourcedTotal = (value: number, source: string | undefined): number =>
      isAggregated || shouldIncludeSource(source) ? value : 0;

    // Calculate totals for each series
    // Demand has no source filter as it's a derived value
    const demandTotal = filteredData.reduce((sum, p) => sum + p.demand, 0);
    const behindMeterPVTotal = filteredData.reduce(
      (sum, p) => sum + p.behindMeterPV,
      0
    );
    const generationTotal = filteredData.reduce((sum, p) => {
      return sum + sourcedTotal(p.generation, p.generationSource);
    }, 0);
    const importTotal = filteredData.reduce((sum, p) => {
      return sum + sourcedTotal(p.import, p.importSource);
    }, 0);
    const exportTotal = filteredData.reduce((sum, p) => {
      return sum + sourcedTotal(p.export, p.exportSource);
    }, 0);
    const hasExportMetered = filteredData.some(
      (p) => (p.exportMetered ?? 0) > 0
    );
    const exportMeteredTotal = hasExportMetered
      ? filteredData.reduce((sum, p) => sum + (p.exportMetered ?? 0), 0)
      : 0;
    const hasPvAuxDemand = filteredData.some((p) => (p.pvAuxDemand ?? 0) > 0);
    const pvAuxDemandTotal = hasPvAuxDemand
      ? filteredData.reduce((sum, p) => sum + (p.pvAuxDemand ?? 0), 0)
      : 0;

    const hasNetworkImport = filteredData.some(
      (p) => (p.networkImport ?? 0) > 0
    );
    const networkImportTotal = hasNetworkImport
      ? filteredData.reduce((sum, p) => sum + (p.networkImport ?? 0), 0)
      : 0;

    // Fold the settlement-period curtailment flags onto whichever mark renders
    // them - the period itself in "none" mode, the bucket's bar otherwise.
    // `curtailmentSet` stays at raw resolution for the PR exclusions below.
    const rawTsToBarX = buildRawTsToBarX(rawPoints, aggregationMode, formatters);
    const curtailment = bucketCurtailment(
      curtailmentData,
      rawTsToBarX,
      formatters
    );
    const curtailmentSet = curtailment.curtailedTimestamps;

    // Track whether we need a "Periods" y-axis (for availability and/or curtailment)
    const needsPeriodsAxis = curtailment.totalCount > 0;
    // Will be set later by the availability code if it also needs the axis
    let periodsYAxisIndex: number | null = needsPeriodsAxis
      ? hasBattery
        ? 2
        : 1
      : null;

     
    const series: any[] = isImportOnly
      ? [
          {
            name: `Network Import (${networkImportTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
            type: "line",
            step: "end",
            data: filteredData.map((p) => [
              formatters.shiftToSiteTimezone(p.x),
              p.networkImport ?? 0,
            ]),
            itemStyle: { color: colors.networkImport },
            lineStyle: { width: 2 },
            symbol: "none",
          },
        ]
      : [
          {
            name: `Generation (${generationTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
            type: "line",
            step: "end",
            ...(needsPeriodsAxis ? { stack: "curtailment" } : {}),
            data: filteredData.map((p) => [
              formatters.shiftToSiteTimezone(p.x),
              sourced(p.generation, p.generationSource),
            ]),
            itemStyle: { color: colors.generation },
            lineStyle: { width: 2 },
            symbol: "none",
            custom: { source: filteredData.map((p) => p.generationSource) },
          },
          // Curtailment period markers: shaded areas like PV Availability series.
          // Both series resolve through the bar-x maps rather than the mark's
          // own timestamp, so under aggregation a bucket carries the count and
          // kWh of every settlement period it contains instead of only its first.
          ...(needsPeriodsAxis
            ? [
                {
                  name: `PR: Curtailed period (${curtailment.totalCount} ${curtailment.totalCount === 1 ? "period" : "periods"})`,
                  type: "line" as const,
                  step: "end" as const,
                  yAxisIndex: periodsYAxisIndex,
                  data: filteredData.map((p) => {
                    const barX = formatters.shiftToSiteTimezone(p.x);
                    return [barX, curtailment.countByBarX.get(barX) ?? 0] as [
                      number,
                      number,
                    ];
                  }),
                  itemStyle: { color: "#f5a623", opacity: isAggregated ? 0.5 : 1 },
                  lineStyle: { width: 0 },
                  areaStyle: { color: "#f5a623", opacity: 0.18 },
                  symbol: "none",
                  silent: true,
                },
                {
                  name: `PR: Estimated curtailed generation (${curtailment.totalEst.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
                  type: "line" as const,
                  step: "end" as const,
                  stack: "curtailment",
                  data: filteredData.map((p) => {
                    const barX = formatters.shiftToSiteTimezone(p.x);
                    return [barX, curtailment.estByBarX.get(barX) ?? null] as [
                      number,
                      number | null,
                    ];
                  }),
                  itemStyle: { color: "#e8a07a" },
                  lineStyle: { width: 2 },
                  symbol: "none",
                },
              ]
            : []),
          {
            name: `Import (${importTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
            type: "line",
            step: "end",
            data: filteredData.map((p) => [
              formatters.shiftToSiteTimezone(p.x),
              sourced(p.import, p.importSource),
            ]),
            itemStyle: { color: colors.import },
            lineStyle: { width: 2 },
            symbol: "none",
            custom: { source: filteredData.map((p) => p.importSource) },
          },
          {
            name: `Export (${exportTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
            type: "line",
            step: "end",
            data: filteredData.map((p) => [
              formatters.shiftToSiteTimezone(p.x),
              sourced(p.export, p.exportSource),
            ]),
            itemStyle: { color: colors.export },
            lineStyle: { width: 2 },
            symbol: "none",
            custom: { source: filteredData.map((p) => p.exportSource) },
          },
          ...(hasAnyZeroExport
            ? [
                {
                  name: `Export (raw) (${filteredData.reduce((sum, p) => sum + sourcedTotal(p.exportRaw, p.exportSource), 0).toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
                  type: "line",
                  step: "end",
                  data: filteredData.map((p) => [
                    formatters.shiftToSiteTimezone(p.x),
                    sourced(p.exportRaw, p.exportSource),
                  ]),
                  itemStyle: { color: colors.exportRaw },
                  lineStyle: { width: 2 },
                  symbol: "none",
                  custom: {
                    source: filteredData.map((p) => p.exportSource),
                  },
                },
              ]
            : []),
          ...(hasExportMetered
            ? [
                {
                  name: `Export metered (supplier) (${exportMeteredTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
                  type: "line",
                  step: "end",
                  data: filteredData.map((p) => [
                    formatters.shiftToSiteTimezone(p.x),
                    p.exportMetered ?? 0,
                  ]),
                  itemStyle: { color: colors.exportMetered },
                  lineStyle: { width: 2 },
                  symbol: "none",
                },
              ]
            : []),
          ...(hasNetworkImport
            ? [
                {
                  name: `Network Import (${networkImportTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
                  type: "line",
                  step: "end",
                  data: filteredData.map((p) => [
                    formatters.shiftToSiteTimezone(p.x),
                    p.networkImport ?? 0,
                  ]),
                  itemStyle: { color: colors.networkImport },
                  lineStyle: { width: 2 },
                  symbol: "none",
                },
              ]
            : []),
          {
            name: `Demand (${demandTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
            type: "line",
            step: "end",
            data: filteredData.map((p) => [
              formatters.shiftToSiteTimezone(p.x),
              p.demand,
            ]),
            itemStyle: { color: colors.demand },
            lineStyle: { width: 2 },
            symbol: "none",
          },
          {
            name: `On-site direct from PV (${behindMeterPVTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
            type: "line",
            step: "end",
            data: filteredData.map((p) => [
              formatters.shiftToSiteTimezone(p.x),
              p.behindMeterPV,
            ]),
            itemStyle: { color: colors.behindMeterPV },
            lineStyle: { width: 2 },
            symbol: "none",
          },
          // Grid drawn by the PV equipment itself (inverter parasitic load). Only
          // rendered when non-zero - most sites never register any, and a permanently
          // flat series would just add legend noise.
          ...(hasPvAuxDemand
            ? [
                {
                  name: `PV aux demand (${pvAuxDemandTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
                  type: "line",
                  step: "end",
                  data: filteredData.map((p) => [
                    formatters.shiftToSiteTimezone(p.x),
                    p.pvAuxDemand ?? 0,
                  ]),
                  itemStyle: { color: colors.pvAuxDemand },
                  lineStyle: { width: 2 },
                  symbol: "none",
                },
              ]
            : []),
        ];

    // Only add battery series if site has battery (and not import-only)
    if (hasBattery && !isImportOnly) {
      const batteryChargeFromPVTotal = filteredData.reduce(
        (sum, p) => sum + p.batteryChargeFromPV,
        0
      );
      const behindMeterBatteryTotal = filteredData.reduce(
        (sum, p) => sum + p.behindMeterBattery,
        0
      );
      const batteryChargeFromGridTotal = filteredData.reduce(
        (sum, p) => sum + p.batteryChargeFromGrid,
        0
      );
      const batteryDischargeGridTotal = filteredData.reduce(
        (sum, p) => sum + p.batteryDischargeGrid,
        0
      );
      const batteryAuxDemandTotal = filteredData.reduce(
        (sum, p) => sum + p.batteryAuxDemand,
        0
      );
      // State of charge, not a flow: summing it across marks produces a number
      // that scales with the mark count (~48x smaller once daily buckets replace
      // half-hours). Report the window's peak, which is the same figure at every
      // aggregation, and read it off the raw periods so bucketing can't hide it.
      const batteryFinalChargePeak = rawPoints.reduce(
        (peak, p) =>
          shouldIncludeSource(p.batteryFinalChargeSource)
            ? Math.max(peak, p.batteryFinalCharge)
            : peak,
        0
      );

      series.push(
        {
          name: `Battery charge from PV (${batteryChargeFromPVTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
          type: "line",
          step: "end",
          data: filteredData.map((p) => [
            formatters.shiftToSiteTimezone(p.x),
            p.batteryChargeFromPV,
          ]),
          itemStyle: { color: colors.batteryChargeFromPV },
          lineStyle: { width: 2 },
          symbol: "none",
        },
        {
          name: `On-site battery discharge from PV (${behindMeterBatteryTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
          type: "line",
          step: "end",
          data: filteredData.map((p) => [
            formatters.shiftToSiteTimezone(p.x),
            p.behindMeterBattery,
          ]),
          itemStyle: { color: colors.behindMeterBattery },
          lineStyle: { width: 2 },
          symbol: "none",
        },
        {
          name: `Battery charge from grid (${batteryChargeFromGridTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
          type: "line",
          step: "end",
          data: filteredData.map((p) => [
            formatters.shiftToSiteTimezone(p.x),
            p.batteryChargeFromGrid,
          ]),
          itemStyle: { color: colors.batteryChargeFromGrid },
          lineStyle: { width: 2 },
          symbol: "none",
        },
        {
          name: `Battery discharge of grid energy (${batteryDischargeGridTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
          type: "line",
          step: "end",
          data: filteredData.map((p) => [
            formatters.shiftToSiteTimezone(p.x),
            p.batteryDischargeGrid,
          ]),
          itemStyle: { color: colors.batteryDischargeGrid },
          lineStyle: { width: 2 },
          symbol: "none",
        },
        {
          name: `Battery aux demand (${batteryAuxDemandTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
          type: "line",
          step: "end",
          data: filteredData.map((p) => [
            formatters.shiftToSiteTimezone(p.x),
            p.batteryAuxDemand,
          ]),
          itemStyle: { color: colors.batteryAuxDemand },
          lineStyle: { width: 2 },
          symbol: "none",
        },
        {
          name: `Battery Final Charge (peak ${batteryFinalChargePeak.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
          type: "line",
          step: "end",
          yAxisIndex: 1,
          data: filteredData.map((p) => [
            formatters.shiftToSiteTimezone(p.x),
            sourced(p.batteryFinalCharge, p.batteryFinalChargeSource),
          ]),
          itemStyle: { color: colors.batteryFinalCharge },
          lineStyle: { width: 2 },
          symbol: "none",
          custom: {
            source: filteredData.map((p) => p.batteryFinalChargeSource),
          },
        }
      );
    }

    // Remap forecast data from abstract 2024 timestamps onto the actual date range
    const remapForecast = (forecastData: ChartDataPoint[]) => {
      const lookup = new Map<string, ChartDataPoint>();
      for (const point of forecastData) {
        const d = new Date(point.x);
        const key = `${d.getUTCMonth()}-${d.getUTCDate()}-${d.getUTCHours()}-${d.getUTCMinutes()}`;
        lookup.set(key, point);
      }
      const result: ChartDataPoint[] = [];
      if (startDate && endDate) {
        const HALF_HOUR_MS = 30 * 60 * 1000;
        let currentMs = Math.ceil(startMs / HALF_HOUR_MS) * HALF_HOUR_MS;
        while (currentMs <= endMs) {
          const d = new Date(currentMs);
          const key = `${d.getUTCMonth()}-${d.getUTCDate()}-${d.getUTCHours()}-${d.getUTCMinutes()}`;
          const forecastPoint = lookup.get(key);
          if (forecastPoint) {
            result.push({ ...forecastPoint, x: d.toISOString() });
          }
          currentMs += HALF_HOUR_MS;
        }
      }
      // Aggregate by the same time periods if needed
      if (aggregationMode !== "none" && result.length > 0) {
        const buckets = new Map<
          string,
          { firstX: string; fGeneration: number; fDemand: number }
        >();
        for (const point of result) {
          const dateStr = formatters.toDateString(point.x);
          const key = getBucketKey(dateStr, aggregationMode);
          let bucket = buckets.get(key);
          if (!bucket) {
            bucket = { firstX: point.x, fGeneration: 0, fDemand: 0 };
            buckets.set(key, bucket);
          }
          bucket.fGeneration += point.fGeneration;
          bucket.fDemand += point.fDemand;
        }
        return Array.from(buckets.values()).map((b) => ({
          ...forecastData[0],
          x: b.firstX,
          fGeneration: b.fGeneration,
          fDemand: b.fDemand,
        }));
      }
      return result;
    };

    // Hoisted so PR computation (below bar conversion) can access it
    let inrangeDataForPR: [number, number][] = [];
    // Original weather actuals indices for PR bucketing (avoids double timezone
    // conversion that occurs when toDateString is called on shifted timestamps).
    let waIndicesForPR: number[] = [];

    // Add forecast overlay series (P50 + P90 generation, demand from P50)
    const hasP50 = forecastP50Data && forecastP50Data.length > 0;
    const hasP90 = forecastP90Data && forecastP90Data.length > 0;
    if (hasP50 || hasP90) {
      const filteredP50 = hasP50 ? remapForecast(forecastP50Data) : [];
      const filteredP90 = hasP90 ? remapForecast(forecastP90Data) : [];

      // Demand from P50 (same as P90)
      const demandSource = filteredP50.length > 0 ? filteredP50 : filteredP90;
      const forecastDemandTotal = demandSource.reduce(
        (sum, p) => sum + p.fDemand,
        0
      );
      const forecastDemandSeries = {
        name: `Forecast Demand (${forecastDemandTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
        type: "line",
        step: "end",
        data: demandSource.map((p: ChartDataPoint) => [
          formatters.shiftToSiteTimezone(p.x),
          p.fDemand,
        ]),
        itemStyle: { color: colors.forecastDemand },
        lineStyle: { width: 2 },
        symbol: "none",
      };

      // Insert Forecast Demand after Demand
      const demandIdx = series.findIndex((s: any) =>
        s.name.startsWith("Demand (")
      );
      if (demandIdx !== -1) {
        series.splice(demandIdx + 1, 0, forecastDemandSeries);
      } else {
        series.push(forecastDemandSeries);
      }

      // Insert generation forecasts after Generation (P90 first so P50 ends up directly after Generation)
      if (!isImportOnly) {
        const genSeriesList: any[] = [];

        if (filteredP90.length > 0) {
          const p90Total = filteredP90.reduce(
            (sum, p) => sum + p.fGeneration,
            0
          );
          genSeriesList.push({
            name: `Forecast Generation (P90) (${p90Total.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
            type: "line",
            step: "end",
            data: filteredP90.map((p: ChartDataPoint) => [
              formatters.shiftToSiteTimezone(p.x),
              p.fGeneration,
            ]),
            itemStyle: { color: colors.forecastGenerationP90 },
            lineStyle: { width: 2 },
            symbol: "none",
          });
        }

        if (filteredP50.length > 0) {
          const p50Total = filteredP50.reduce(
            (sum, p) => sum + p.fGeneration,
            0
          );
          genSeriesList.push({
            name: `Forecast Generation (P50) (${p50Total.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
            type: "line",
            step: "end",
            data: filteredP50.map((p: ChartDataPoint) => [
              formatters.shiftToSiteTimezone(p.x),
              p.fGeneration,
            ]),
            itemStyle: { color: colors.forecastGeneration },
            lineStyle: { width: 2 },
            symbol: "none",
          });
        }

        // Add weather actuals series if available
        if (weatherActualsData && weatherActualsData.x.length > 0) {
          // Filter to visible date range (if "All available", include everything)
          const hasDateRange = startDate && endDate;
          const waIndices: number[] = [];
          if (hasDateRange) {
            const { startMs: waStartMs, endMs: waEndMs } =
              formatters.dateRangeToTimestamps(startDate, endDate);
            for (let i = 0; i < weatherActualsData.x.length; i++) {
              const ts = formatters.getTime(weatherActualsData.x[i]);
              if (ts >= waStartMs && ts <= waEndMs) {
                waIndices.push(i);
              }
            }
          } else {
            for (let i = 0; i < weatherActualsData.x.length; i++) {
              waIndices.push(i);
            }
          }

          if (waIndices.length > 0) {
            // Build raw data points for inrange series
            let inrangeData: [number, number][] = waIndices.map((i) => [
              formatters.shiftToSiteTimezone(weatherActualsData.x[i]),
              waInrangeSeries![i],
            ]);

            // Aggregate if needed (sum energy values per bucket)
            if (aggregationMode !== "none") {
              // Bucket key -> bar chart timestamp, so weather actuals x values
              // align exactly with the bar series
              const barBucketTs = buildBucketTsMap(
                filteredData,
                aggregationMode,
                formatters
              );

              const aggregateWaSeries = (
                data: [number, number][]
              ): [number, number][] => {
                const buckets = new Map<string, number>();
                const bucketTs = new Map<string, number>();
                for (const [ts, val] of data) {
                  const dateStr = formatters.toDateString(ts);
                  const key = getBucketKey(dateStr, aggregationMode);
                  buckets.set(key, (buckets.get(key) || 0) + val);
                  if (!bucketTs.has(key)) bucketTs.set(key, ts);
                }
                return Array.from(buckets.entries())
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, val]) => [
                    barBucketTs.get(key) ?? bucketTs.get(key)!,
                    val,
                  ]);
              };
              inrangeData = aggregateWaSeries(inrangeData);
            }
            inrangeDataForPR = inrangeData;
            waIndicesForPR = waIndices;

            const inrangeTotal = inrangeData.reduce(
              (sum, [, val]) => sum + val,
              0
            );
            genSeriesList.push({
              name: `Expected Gen. (Meteonorm) (${inrangeTotal.toLocaleString(undefined, { maximumFractionDigits: 3 })} kWh)`,
              type: "line",
              step: aggregationMode === "none" ? "end" : undefined,
              data: inrangeData,
              itemStyle: { color: colors.weatherActualsInrange },
              lineStyle: { width: 2 },
              symbol: "none",
            });
          }
        }

        // Insert after Generation — P50 first, then P90, then weather actuals
        const genIdx = series.findIndex((s: any) =>
          s.name.startsWith("Generation (")
        );
        if (genIdx !== -1) {
          series.splice(genIdx + 1, 0, ...genSeriesList);
        } else {
          series.push(...genSeriesList);
        }
      }
    }

    // Add "PV Availability: Expected gen. period" and "Unavailable gen. period"
    // series using the backend-provided expectedGenerationPeriods mask.
    // This mask is computed by the same build_expected_generation_mask() function
    // used by the backend availability calculation, ensuring the chart matches.
    let unavailYAxisIndex: number | null = null;
    let expectedPeriodCount = 0;
    let unavailablePeriodCount = 0;
    // If curtailment already reserved the periods axis, availability uses the same one
    if (
      expectedGenerationPeriods &&
      forecastP50Data &&
      forecastP50Data.length > 0 &&
      !isImportOnly
    ) {
      // Build a lookup from abstract 2024 month-day-hour-minute to the mask index
      const maskByKey = new Map<string, boolean>();
      for (
        let i = 0;
        i < forecastP50Data.length && i < expectedGenerationPeriods.length;
        i++
      ) {
        const d = new Date(forecastP50Data[i].x);
        const key = `${d.getUTCMonth()}-${d.getUTCDate()}-${d.getUTCHours()}-${d.getUTCMinutes()}`;
        maskByKey.set(key, expectedGenerationPeriods[i]);
        // For 15-min settlement periods (NLD): extend the 30-min forecast mask
        // to also cover the :15/:45 period within the same half hour
        const d15 = new Date(d.getTime() + 15 * 60 * 1000);
        const key15 = `${d15.getUTCMonth()}-${d15.getUTCDate()}-${d15.getUTCHours()}-${d15.getUTCMinutes()}`;
        if (!maskByKey.has(key15)) {
          maskByKey.set(key15, expectedGenerationPeriods[i]);
        }
      }

      // Remap mask into the visible date range using the actual settlement
      // period from the data (not hardcoded 30 min — NLD uses 15 min).
      type PeriodInfo = { ts: number; isExpected: boolean; dateStr: string };
      const periodInfos: PeriodInfo[] = [];
      if (startDate && endDate && rawPoints.length >= 2) {
        // Infer the settlement period from consecutive RAW readings. Reading it
        // off `filteredData` under aggregation gives a day/week/month, which
        // walks the mask at one sample per bucket - always a midnight, never an
        // expected-generation period - so every bucket counted zero.
        const periodMs = inferSettlementPeriodMs(rawPoints);

        let currentMs = Math.ceil(startMs / periodMs) * periodMs;
        while (currentMs <= endMs) {
          const d = new Date(currentMs);
          const key = `${d.getUTCMonth()}-${d.getUTCDate()}-${d.getUTCHours()}-${d.getUTCMinutes()}`;
          const isExpected = maskByKey.get(key) ?? false;
          const ts = formatters.shiftToSiteTimezone(d.toISOString());
          periodInfos.push({
            ts,
            isExpected,
            dateStr: formatters.toDateString(d.toISOString()),
          });
          currentMs += periodMs;
        }
      }

      if (periodInfos.length > 0) {
        // Start from backend-provided unavailable periods (gen_drop_to_zero),
        // then augment with periods covered by other health events (inverter
        // outages, battery stuck, etc.) so the availability % reflects all
        // displayed events.
        const unavailableSet = new Set<number>();
        if (data.unavailablePeriods) {
          for (const iso of data.unavailablePeriods) {
            unavailableSet.add(formatters.shiftToSiteTimezone(iso));
          }
        }

        // Add unavailable periods from chart health events.
        // A period is unavailable if any part of an event span overlaps it.
        // Period [ts, ts+periodMs) overlaps span [sMs, eMs) when ts < eMs && ts+periodMs > sMs.
        if (eventLogEvents && periodInfos.length >= 2) {
          const periodMs = periodInfos[1].ts - periodInfos[0].ts;
          const expectedPeriods = periodInfos.filter((p) => p.isExpected);

          // Collect all event time ranges (span-level for inverter outages).
          // For unresolved events, extend to the last expected period so the
          // availability % reflects the ongoing outage.
          type TimeRange = { sMs: number; eMs: number };
          const eventRanges: TimeRange[] = [];
          const lastPeriodEndMs =
            expectedPeriods.length > 0
              ? expectedPeriods[expectedPeriods.length - 1].ts + periodMs
              : 0;

          for (const event of eventLogEvents) {
            if (event.checkType === "gen_drop_to_zero") continue;
            // Only auto-detected PV outage events affect availability —
            // manual/event_log entries are informational annotations,
            // and battery outage events don't affect PV availability.
            if (event.impactType !== "unplanned_pv_outage") continue;
            if (event.category === "event_log") continue;

            if (
              event.checkType === "inverter_outage_monthly" &&
              Array.isArray(event.details?.outage_spans)
            ) {
              for (const span of event.details.outage_spans as OutageSpan[]) {
                eventRanges.push({
                  sMs: formatters.shiftToSiteTimezone(span.start),
                  eMs: formatters.shiftToSiteTimezone(span.end),
                });
              }
            } else if (event.detectedAt) {
              const eMs = event.resolvedAt
                ? formatters.shiftToSiteTimezone(event.resolvedAt)
                : lastPeriodEndMs;
              eventRanges.push({
                sMs: formatters.shiftToSiteTimezone(event.detectedAt),
                eMs,
              });
            }
          }

          if (eventRanges.length > 0) {
            for (const period of expectedPeriods) {
              const periodEnd = period.ts + periodMs;
              for (const range of eventRanges) {
                if (period.ts < range.eMs && periodEnd > range.sMs) {
                  unavailableSet.add(period.ts);
                  break;
                }
              }
            }
          }
        }

        // Both expected and unavailable series share a dedicated "Periods" axis
        // (same axis as curtailment periods if it exists)
        unavailYAxisIndex = periodsYAxisIndex ?? (hasBattery ? 2 : 1);
        periodsYAxisIndex = unavailYAxisIndex;

        if (aggregationMode === "none") {
          const expectedPeriodsData: [number, number][] = [];
          const unavailableData: [number, number][] = [];

          for (const info of periodInfos) {
            expectedPeriodsData.push([info.ts, info.isExpected ? 1 : 0]);
            if (info.isExpected) {
              expectedPeriodCount++;
              const isUnavailable = unavailableSet.has(info.ts);
              if (isUnavailable) unavailablePeriodCount++;
              unavailableData.push([info.ts, isUnavailable ? 1 : 0]);
            } else {
              unavailableData.push([info.ts, 0]);
            }
          }

          const pluralExpected =
            expectedPeriodCount === 1 ? "period" : "periods";
          const pluralUnavail =
            unavailablePeriodCount === 1 ? "period" : "periods";

          series.push({
            name: `PV Availability: Expected gen. period (${expectedPeriodCount} ${pluralExpected})`,
            type: "line",
            step: "end",
            yAxisIndex: unavailYAxisIndex,
            data: expectedPeriodsData,
            itemStyle: { color: colors.expectedGenPeriods },
            lineStyle: { width: 0 },
            areaStyle: { color: colors.expectedGenPeriods, opacity: 0.18 },
            symbol: "none",
            silent: true,
          });

          series.push({
            name: `PV Availability: Unavailable gen. period (${unavailablePeriodCount} ${pluralUnavail})`,
            type: "line",
            step: "end",
            data: unavailableData,
            yAxisIndex: unavailYAxisIndex,
            itemStyle: { color: colors.unavailableGeneration },
            lineStyle: { width: 0 },
            areaStyle: { color: colors.unavailableGeneration, opacity: 0.6 },
            symbol: "none",
            silent: true,
          });
        } else {
          // Aggregated mode: count expected/unavailable periods per bucket
          const barBucketTs = buildBucketTsMap(
            filteredData,
            aggregationMode,
            formatters
          );

          const expectedBuckets = new Map<
            string,
            { ts: number; count: number }
          >();
          const unavailableBuckets = new Map<
            string,
            { ts: number; count: number; expectedCount: number }
          >();

          for (const info of periodInfos) {
            const key = getBucketKey(info.dateStr, aggregationMode);
            if (!expectedBuckets.has(key)) {
              expectedBuckets.set(key, {
                ts: barBucketTs.get(key) ?? info.ts,
                count: 0,
              });
            }
            if (!unavailableBuckets.has(key)) {
              unavailableBuckets.set(key, {
                ts: barBucketTs.get(key) ?? info.ts,
                count: 0,
                expectedCount: 0,
              });
            }

            if (info.isExpected) {
              expectedPeriodCount++;
              expectedBuckets.get(key)!.count += 1;
              unavailableBuckets.get(key)!.expectedCount += 1;
              if (unavailableSet.has(info.ts)) {
                unavailablePeriodCount++;
                unavailableBuckets.get(key)!.count += 1;
              }
            }
          }

          const expectedPeriodsData = Array.from(expectedBuckets.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, { ts, count }]) => [ts, count] as [number, number]);

          const unavailableData = Array.from(unavailableBuckets.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, { ts, count }]) => [ts, count] as [number, number]);

          const unavailPctMap = new Map<
            number,
            { count: number; expectedCount: number }
          >();
          for (const [, { ts, count, expectedCount }] of unavailableBuckets) {
            unavailPctMap.set(ts, { count, expectedCount });
          }

          const pluralExpected =
            expectedPeriodCount === 1 ? "period" : "periods";
          const pluralUnavail =
            unavailablePeriodCount === 1 ? "period" : "periods";

          series.push({
            name: `PV Availability: Expected gen. period (${expectedPeriodCount} ${pluralExpected})`,
            type: "bar",
            data: expectedPeriodsData,
            yAxisIndex: unavailYAxisIndex,
            itemStyle: { color: colors.expectedGenPeriods, opacity: 0.4 },
            symbol: "none",
          });

          series.push({
            name: `PV Availability: Unavailable gen. period (${unavailablePeriodCount} ${pluralUnavail})`,
            type: "bar",
            data: unavailableData,
            yAxisIndex: unavailYAxisIndex,
            itemStyle: { color: colors.unavailableGeneration, opacity: 0.6 },
            symbol: "none",
            custom: { unavailPctMap },
          });
        }
      }
    }

    // Switch to bar charts for aggregated data (skip series that manage their own type)
    if (isAggregated) {
      series.forEach((s: any) => {
        if (s.name.startsWith("Health events")) return;
        s.type = "bar";
        delete s.step;
        delete s.symbol;
        delete s.lineStyle;
      });
    }

    // Compute trailing-window Performance Ratio (always a line, never a bar)
    let prYAxisIndex: number | null = null;
    if (inrangeDataForPR.length > 0 && !isImportOnly) {
      const windowConfig = {
        none: { points: 7, label: "7 days" },
        daily: { points: 7, label: "7 days" },
        weekly: { points: 4, label: "4 weeks" },
        monthly: { points: 3, label: "3 months" },
      }[aggregationMode];

      // Aggregate expected and actual generation into per-bucket totals.
      // For raw mode, always aggregate to daily so the PR line is smooth.
      const prBucketMode =
        aggregationMode === "none" ? "daily" : aggregationMode;

      // Build bar-aligned shifted timestamps for PR line x-coordinates.
      // Always overwrite so the last point in each bucket wins - this renders
      // GPR/OPR at the end of each day (the trailing-window value is the PR
      // computed over the period ending at that point).
      const prBarTs = new Map<string, number>();
      for (const point of filteredData) {
        const key = getBucketKey(
          formatters.toDateString(point.x),
          prBucketMode
        );
        prBarTs.set(key, formatters.shiftToSiteTimezone(point.x));
      }

      // Use original (unshifted) weather actuals timestamps for bucketing
      // to avoid double timezone conversion (shiftToSiteTimezone shifts for
      // chart display using browser TZ, but toDateString converts using site
      // TZ — when browser != site TZ, combining them produces wrong dates).
      // GPR uses the contract-ideal denominator (matches site health page);
      // OPR uses the InRange-model expected denominator. Both fall back when
      // the backend hasn't shipped that series yet.
      const buildBuckets = (denominator: number[] | null) => {
        if (denominator == null) return null;
        const expectedByBucket = new Map<
          string,
          { ts: number; val: number }
        >();
        for (const idx of waIndicesForPR) {
          const originalTs = weatherActualsData!.x[idx];
          if (curtailmentSet.has(formatters.getTime(originalTs))) continue;
          const key = getBucketKey(
            formatters.toDateString(originalTs),
            prBucketMode
          );
          const expected = denominator[idx];
          const existing = expectedByBucket.get(key);
          if (existing) {
            existing.val += expected;
          } else {
            const ts =
              prBarTs.get(key) ?? formatters.shiftToSiteTimezone(originalTs);
            expectedByBucket.set(key, { ts, val: expected });
          }
        }
        return expectedByBucket;
      };

      const gprBuckets = buildBuckets(waIdealSeries ?? waInrangeSeries ?? null);
      const oprBuckets = buildBuckets(waInrangeSeries ?? null);

      // Numerator is summed from RAW periods so the curtailment exclusion below
      // matches the denominator's, which drops individual weather-actuals
      // periods. Summing bucketed points instead would test the exclusion
      // against a bucket's first timestamp, keeping curtailed generation in the
      // numerator while the denominator lost it - inflating GPR/OPR whenever
      // aggregation was on.
      const actualByBucket = new Map<string, number>();
      for (const point of rawPoints) {
        // Exclude curtailed periods from actual generation in trailing window
        if (curtailmentSet.has(formatters.getTime(point.x))) continue;
        const gen = shouldIncludeSource(point.generationSource)
          ? point.generation
          : 0;
        const key = getBucketKey(
          formatters.toDateString(point.x),
          prBucketMode
        );
        actualByBucket.set(key, (actualByBucket.get(key) || 0) + gen);
      }

      const computeRollingPr = (
        buckets: Map<string, { ts: number; val: number }> | null
      ): [number, number | null][] | null => {
        if (!buckets) return null;
        const sortedBuckets = Array.from(buckets.entries()).sort(
          ([a], [b]) => a.localeCompare(b)
        );
        const windowSize = windowConfig.points;
        const prData: [number, number | null][] = [];
        let actualSum = 0;
        let expectedSum = 0;

        for (let i = 0; i < sortedBuckets.length; i++) {
          const [key, { ts, val: expected }] = sortedBuckets[i];
          actualSum += actualByBucket.get(key) || 0;
          expectedSum += expected;

          if (i >= windowSize) {
            const [oldKey, { val: oldExp }] = sortedBuckets[i - windowSize];
            actualSum -= actualByBucket.get(oldKey) || 0;
            expectedSum -= oldExp;
          }

          if (i < windowSize - 1 || expectedSum <= 0) {
            prData.push([ts, null]);
          } else {
            prData.push([ts, (actualSum / expectedSum) * 100]);
          }
        }
        return prData;
      };

      const gprData = computeRollingPr(gprBuckets);
      const oprData = computeRollingPr(oprBuckets);

      const hasValidGpr = gprData?.some(([, v]) => v !== null) ?? false;
      const hasValidOpr = oprData?.some(([, v]) => v !== null) ?? false;
      if (hasValidGpr || hasValidOpr) {
        // Account for battery axis and unavailable generation axis
        let nextAxisIdx = 1;
        if (hasBattery) nextAxisIdx++;
        if (periodsYAxisIndex !== null) nextAxisIdx++;
        prYAxisIndex = nextAxisIdx;
        if (hasValidGpr && gprData) {
          series.push({
            name: `GPR (Meteonorm) (Trailing ${windowConfig.label})`,
            type: "line",
            data: gprData,
            yAxisIndex: prYAxisIndex,
            itemStyle: { color: colors.performanceRatio },
            lineStyle: { width: 2 },
            symbol: "none",
            connectNulls: false,
          });
        }
        if (hasValidOpr && oprData) {
          series.push({
            name: `OPR (Meteonorm) (Trailing ${windowConfig.label})`,
            type: "line",
            data: oprData,
            yAxisIndex: prYAxisIndex,
            itemStyle: { color: colors.weatherActualsInrange },
            lineStyle: { width: 2, type: "dashed" },
            symbol: "none",
            connectNulls: false,
          });
        }
      }
    }

    // Add "Health events" series with markArea overlays so events can be
    // toggled on/off via the legend independently of other series.
    if (eventLogEvents && eventLogEvents.length > 0) {
      const { startMs: chartStartMs, endMs: chartEndMs } =
        startDate && endDate
          ? formatters.dateRangeToTimestamps(startDate, endDate)
          : { startMs: 0, endMs: Infinity };

      const visibleEvents = eventLogEvents.filter((event) => {
        const detectedMs = formatters.shiftToSiteTimezone(event.detectedAt);
        const resolvedMs = event.resolvedAt
          ? formatters.shiftToSiteTimezone(event.resolvedAt)
          : chartEndMs;
        if (detectedMs >= chartEndMs || resolvedMs <= chartStartMs) return false;
        // Filter by esys when one is selected (site-level events with no esysUuid always show)
        if (selectedEsysUuid && event.esysUuid && event.esysUuid !== selectedEsysUuid) return false;
        return true;
      });

      // In aggregated modes, snap markArea regions to bucket boundaries so
      // that short events still produce a visible shaded region (e.g. a
      // 2-hour event should shade the entire month bar in monthly view).
      const eventBucketTs = buildBucketTsMap(
        filteredData,
        aggregationMode,
        formatters
      );

      const snapToBucket = (tsMs: number): number => {
        if (aggregationMode === "none") return tsMs;
        const key = getBucketKey(
          formatters.toDateString(tsMs),
          aggregationMode
        );
        return eventBucketTs.get(key) ?? tsMs;
      };

      const getBucketEndMs = (tsMs: number): number => {
        if (aggregationMode === "none") return tsMs;
        const key = getBucketKey(
          formatters.toDateString(tsMs),
          aggregationMode
        );
        // One bar per bucket, so its x is both the first and the last point
        const lastTs = eventBucketTs.get(key) ?? tsMs;
        // Extend past the last point to cover the full bar width
        if (aggregationMode === "monthly") {
          return lastTs + 15 * 24 * 60 * 60 * 1000; // ~half month forward
        } else if (aggregationMode === "weekly") {
          return lastTs + 4 * 24 * 60 * 60 * 1000;
        } else {
          return lastTs + 12 * 60 * 60 * 1000;
        }
      };

      // Derive settlement period from raw data point spacing (handles both
      // 30-min UK and 15-min NLD sites), with 30-min fallback.
      const settlementPeriodMs = inferSettlementPeriodMs(rawPoints);

      const markAreaData = visibleEvents.flatMap((event) => {
        const eventColor =
          event.severity === "red"
            ? "rgba(255, 0, 0, 0.15)"
            : "rgba(255, 165, 0, 0.15)";

        // For inverter outage events, render each individual outage span
        // instead of shading the full detectedAt → resolvedAt month block.
        if (
          event.checkType === "inverter_outage_monthly" &&
          event.details?.outage_spans &&
          Array.isArray(event.details.outage_spans)
        ) {
          return (
            event.details.outage_spans as OutageSpan[]
          ).map((span) => {
            const spanStartMs = formatters.shiftToSiteTimezone(span.start);
            const spanEndMs = formatters.shiftToSiteTimezone(span.end);
            // Snap to settlement period boundaries so shading aligns with
            // the chart grid.
            const startMs =
              aggregationMode === "none"
                ? Math.floor(spanStartMs / settlementPeriodMs) * settlementPeriodMs
                : snapToBucket(spanStartMs);
            const endMs =
              aggregationMode === "none"
                ? Math.ceil(spanEndMs / settlementPeriodMs) * settlementPeriodMs
                : getBucketEndMs(spanEndMs);
            return [
              {
                xAxis: startMs,
                itemStyle: { color: eventColor },
                name: `${span.serial_number} offline ${span.duration_min} min`,
              },
              { xAxis: endMs },
            ];
          });
        }

        const detectedMs = formatters.shiftToSiteTimezone(event.detectedAt);
        const resolvedMs = event.resolvedAt
          ? formatters.shiftToSiteTimezone(event.resolvedAt)
          : chartEndMs;
        // Snap to settlement period boundaries or aggregation buckets
        const startMs =
          aggregationMode === "none"
            ? Math.floor(detectedMs / settlementPeriodMs) * settlementPeriodMs
            : snapToBucket(detectedMs);
        const endMs =
          aggregationMode === "none"
            ? Math.ceil(resolvedMs / settlementPeriodMs) * settlementPeriodMs
            : getBucketEndMs(resolvedMs);
        return [
          [
            {
              xAxis: startMs,
              itemStyle: { color: eventColor },
              name: event.summary || event.notes || "Event",
            },
            { xAxis: endMs },
          ],
        ];
      });

      const eventCount = visibleEvents.length;
      const plural = eventCount === 1 ? "event" : "events";
      series.push({
        name: `Health events (${eventCount} ${plural})`,
        type: "line",
        data: filteredData.map((p) => [
          formatters.shiftToSiteTimezone(p.x),
          null,
        ]),
        itemStyle: { color: colors.healthEvents },
        lineStyle: { width: 0 },
        symbol: "none",
        silent: true,
        ...(markAreaData.length > 0
          ? {
              markArea: {
                silent: false,
                label: { show: false },
                data: markAreaData,
              },
            }
          : {}),
      });
    }

    const isSourceFiltering = selectedSources.length > 0;

    // Order legend into grouped lines:
    // Line 1: Generation + forecast generation
    // Line 2: Demand + forecast demand
    // Line 3: Import + Export
    // Line 4: Everything else
    const seriesNames = series.map((s: any) => s.name as string);
    const findName = (prefix: string) =>
      seriesNames.find((n) => n.startsWith(prefix + " (") || n === prefix);
    const legendGroupPrefixes = [
      ["Health events"],
      ["Generation", "Forecast Generation (P50)", "Forecast Generation (P90)"],
      ["Expected Gen. (Meteonorm)", "GPR (Meteonorm)", "OPR (Meteonorm)"],
      [
        "PV Availability: Expected gen. period",
        "PV Availability: Unavailable gen. period",
      ],
      ["Demand", "Forecast Demand"],
      ["Import", "Export"],
    ];
    const legendRows: string[][] = [];
    const usedNames = new Set<string>();
    for (const group of legendGroupPrefixes) {
      const row = group.map(findName).filter(Boolean) as string[];
      if (row.length > 0) {
        legendRows.push(row);
        row.forEach((n) => usedNames.add(n));
      }
    }
    const remaining = seriesNames.filter((n) => !usedNames.has(n));
    // Split remaining into rows of at most 3 to avoid wrapping
    for (let i = 0; i < remaining.length; i += 3) {
      legendRows.push(remaining.slice(i, i + 3));
    }

    // Reorder series array to match legend row order so chart/tooltip match legend
    const legendOrder = legendRows.flat();
    series.sort((a: any, b: any) => {
      const ai = legendOrder.indexOf(a.name);
      const bi = legendOrder.indexOf(b.name);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    });

    // Use persisted legend selections if available, otherwise use preset-defined visible series
    // When source filtering is active, auto-deselect series without source data
    const activePreset = PRESETS[preset] ?? PRESETS.default;
    const visiblePrefixes =
      hasBattery && activePreset.batteryExtras
        ? [...activePreset.series, ...activePreset.batteryExtras]
        : activePreset.series;
    const legendSelected: Record<string, boolean> = {};
    series.forEach((s: any) => {
      const baseName = s.name.replace(/ \([^)]*\)$/, "");
      const hasSourceData = s.custom?.source !== undefined;
      if (isSourceFiltering && !hasSourceData) {
        legendSelected[s.name] = false;
      } else if (
        legendSelectionsRef.current !== null &&
        baseName in legendSelectionsRef.current
      ) {
        legendSelected[s.name] = legendSelectionsRef.current[baseName];
      } else if (isImportOnly) {
        legendSelected[s.name] = baseName === "Network Import";
      } else {
        legendSelected[s.name] = visiblePrefixes.some(
          (p) => s.name.startsWith(p + " (") || s.name === p
        );
      }
    });

    const getTooltipDateLabel = (
      dataIndex: number,
      shiftedAxisValue: number | undefined
    ) => {
      // Prefer looking up the original (unshifted) timestamp from filteredData
      // so we can format it correctly with timezone info.
      // However, dataIndex is per-series, so when the first tooltip series
      // has a different resolution (e.g. 30-min forecast vs 15-min actuals),
      // the index won't correspond to the right filteredData entry.
      // In that case, fall back to the shifted axis value (format as UTC
      // since the timezone shift has already been applied).
      const originalTimestamp = filteredData[dataIndex]?.x;
      if (originalTimestamp) {
        // Verify the timestamp matches the axis value to avoid index mismatch
        const shiftedOriginal =
          formatters.shiftToSiteTimezone(originalTimestamp);
        if (
          shiftedAxisValue === undefined ||
          Math.abs(shiftedOriginal - shiftedAxisValue) < 60000
        ) {
          if (!isAggregated)
            return formatters.toDateTimeString(originalTimestamp);
          const dateStr = formatters.toDateString(originalTimestamp);
          if (aggregationMode === "monthly") return dateStr.substring(0, 7);
          if (aggregationMode === "weekly") {
            const key = getBucketKey(dateStr, "weekly");
            const [y, m, d] = key.split("-").map(Number);
            const monday = new Date(Date.UTC(y, m - 1, d));
            const sunday = new Date(monday);
            sunday.setUTCDate(sunday.getUTCDate() + 6);
            return `Week of ${key} to ${sunday.toISOString().substring(0, 10)}`;
          }
          return dateStr;
        }
      }

      // Shifted value: format as UTC since timezone shift is already baked in
      if (shiftedAxisValue !== undefined) {
        const d = new Date(shiftedAxisValue);
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
      }

      return "";
    };

    const chartOptions = {
      animation: false,
      tooltip: {
        trigger: "axis",
        extraCssText: "max-width: min(450px, 90vw); white-space: normal; word-wrap: break-word;",
        axisPointer: {
          type: isAggregated ? "shadow" : "line",
        },
        formatter: (params: any) => {
          if (!params || params.length === 0) return "";

          // Sort tooltip entries to match legend order
          const sortedParams = [...params].sort((a: any, b: any) => {
            const ai = legendOrder.indexOf(a.seriesName);
            const bi = legendOrder.indexOf(b.seriesName);
            return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
          });

          // Use the axis timestamp (shifted) when filteredData lookup
          // may not match (e.g. forecast-only points)
          const dateLabel = getTooltipDateLabel(
            sortedParams[0].dataIndex,
            sortedParams[0].value?.[0]
          );
          let tooltip = `<strong>${dateLabel}</strong><br/>`;

          // Identify PR-like series (GPR/OPR, plus any legacy "PR (...)") so we
          // can render them as percentages and always show them in the tooltip
          // whenever they're visible. These series are sparse (one point per
          // bucket) so without this fallback they'd only appear when the user
          // hovers exactly on a data point.
          const isPRLikeName = (name: string) =>
            /^(?:G|O)?PR \(/.test(name);
          const prLikeSeries = series.filter((s: any) =>
            isPRLikeName(s.name)
          );
          const isPRLikeVisible = (s: any) => {
            const baseName = s.name.replace(/ \([^)]*\)$/, "");
            return legendSelectionsRef.current !== null
              ? (legendSelectionsRef.current[baseName] ??
                  legendSelected[s.name] ??
                  false)
              : (legendSelected[s.name] ?? false);
          };
          const prInParams = new Set<string>();
          sortedParams.forEach((param: any) => {
            const seriesObj = series.find(
              (s: any) => s.name === param.seriesName
            );
            const sourceArray = seriesObj?.custom?.source ?? null;
            const source = sourceArray ? sourceArray[param.dataIndex] : "";
            // Extract base name without the trailing total (e.g. "(1,234 kWh)" or "(N periods)")
            const baseName = param.seriesName.replace(/ \([^)]*\)$/, "");
            if (isPRLikeName(param.seriesName)) {
              const prSeriesObj = series.find(
                (s: any) => s.name === param.seriesName
              );
              if (!prSeriesObj || !isPRLikeVisible(prSeriesObj)) return;
              prInParams.add(param.seriesName);
              tooltip += `${param.marker} ${baseName}: ${param.value[1]?.toFixed(1) ?? "-"}%<br/>`;
            } else if (
              param.seriesName.startsWith(
                "PV Availability: Expected gen. period"
              )
            ) {
              if (isAggregated) {
                tooltip += `${param.marker} PV Availability: Expected gen. period: ${param.value[1] ?? 0} periods<br/>`;
              } else {
                // In non-aggregated mode, show a single line: either "Expected" or "Unavailable"
                // by checking the unavailable series value at this timestamp
                const unavailParam = sortedParams.find((p: any) =>
                  p.seriesName?.startsWith(
                    "PV Availability: Unavailable gen. period"
                  )
                );
                const isUnavailable = unavailParam?.value?.[1] === 1;
                if (param.value[1] > 0) {
                  if (isUnavailable && unavailParam) {
                    tooltip += `${unavailParam.marker} PV Availability: Unavailable gen. period<br/>`;
                  } else {
                    tooltip += `${param.marker} PV Availability: Expected gen. period<br/>`;
                  }
                }
              }
            } else if (
              param.seriesName.startsWith(
                "PV Availability: Unavailable gen. period"
              )
            ) {
              if (isAggregated && seriesObj?.custom?.unavailPctMap) {
                const pctInfo = seriesObj.custom.unavailPctMap.get(
                  param.value[0]
                );
                if (pctInfo && pctInfo.expectedCount > 0) {
                  const pct = (
                    (pctInfo.count / pctInfo.expectedCount) *
                    100
                  ).toFixed(1);
                  tooltip += `${param.marker} PV Availability: Unavailable gen. period: ${param.value[1]} periods (${pct}% of expected)<br/>`;
                } else {
                  tooltip += `${param.marker} PV Availability: Unavailable gen. period: ${param.value[1]} periods<br/>`;
                }
              }
              // Non-aggregated: handled above in the Expected gen section
            } else if (param.seriesName.startsWith("PR: Curtailed period")) {
              // Show curtailment period as Yes/No, and only when curtailed
              if (!isAggregated) {
                if (param.value[1] === 1) {
                  tooltip += `${param.marker} PR: Curtailed period: Yes<br/>`;
                }
              } else {
                if (param.value[1] > 0) {
                  tooltip += `${param.marker} PR: Curtailed periods: ${param.value[1]}<br/>`;
                }
              }
            } else if (
              param.seriesName.startsWith("PR: Estimated curtailed generation")
            ) {
              // Only show during curtailed periods (value > 0)
              if (param.value[1] != null && param.value[1] > 0) {
                tooltip += `${param.marker} ${baseName}: ${param.value[1]?.toFixed(3)} kWh<br/>`;
              }
            } else if (param.seriesName.startsWith("Health events")) {
              // Health events series has no data points — skip in tooltip
            } else {
              tooltip += `${param.marker} ${baseName}: ${param.value[1]?.toFixed(3) || 0} kWh${source ? ` (${source})` : ""}<br/>`;
            }
          });

          // For each visible PR-like series that wasn't already in params
          // (hovered timestamp doesn't exactly match a sparse PR point), pick
          // the nearest value within the line's range so the tooltip always
          // surfaces GPR/OPR when they're enabled.
          const hoveredTs = sortedParams[0]?.value?.[0];
          if (hoveredTs != null) {
            for (const prSeriesObj of prLikeSeries) {
              if (prInParams.has(prSeriesObj.name)) continue;
              if (!isPRLikeVisible(prSeriesObj)) continue;
              let nearest: number | null = null;
              let bestDist = Infinity;
              let minPrTs = Infinity;
              let maxPrTs = -Infinity;
              for (const [ts, val] of prSeriesObj.data as [
                number,
                number | null,
              ][]) {
                if (val == null) continue;
                if (ts < minPrTs) minPrTs = ts;
                if (ts > maxPrTs) maxPrTs = ts;
                const dist = Math.abs(ts - hoveredTs);
                if (dist < bestDist) {
                  bestDist = dist;
                  nearest = val;
                }
              }
              if (
                nearest !== null &&
                hoveredTs >= minPrTs &&
                hoveredTs <= maxPrTs
              ) {
                const color =
                  prSeriesObj.itemStyle?.color ?? colors.performanceRatio;
                tooltip += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span> ${prSeriesObj.name}: ${nearest.toFixed(1)}%<br/>`;
              }
            }
          }

          // Show health events that overlap the hovered timestamp/bucket
          if (eventLogEvents && eventLogEvents.length > 0) {
            if (hoveredTs != null) {
              // Expand the hovered timestamp to cover the full settlement
              // period so events starting partway through are included.
              let bucketStartMs = hoveredTs;
              let bucketEndMs = hoveredTs + inferSettlementPeriodMs(rawPoints);
              if (isAggregated) {
                const dateStr = formatters.toDateString(hoveredTs);
                const key = getBucketKey(dateStr, aggregationMode);
                // Compute bucket end from the aggregation period
                if (aggregationMode === "monthly") {
                  const [y, m] = key.split("-").map(Number);
                  bucketStartMs = formatters.shiftToSiteTimezone(
                    new Date(Date.UTC(y, m - 1, 1)).toISOString()
                  );
                  bucketEndMs = formatters.shiftToSiteTimezone(
                    new Date(Date.UTC(y, m, 1)).toISOString()
                  );
                } else if (aggregationMode === "weekly") {
                  const [y, m, d] = key.split("-").map(Number);
                  bucketStartMs = formatters.shiftToSiteTimezone(
                    new Date(Date.UTC(y, m - 1, d)).toISOString()
                  );
                  bucketEndMs = formatters.shiftToSiteTimezone(
                    new Date(Date.UTC(y, m - 1, d + 7)).toISOString()
                  );
                } else if (aggregationMode === "daily") {
                  const [y, m, d] = key.split("-").map(Number);
                  bucketStartMs = formatters.shiftToSiteTimezone(
                    new Date(Date.UTC(y, m - 1, d)).toISOString()
                  );
                  bucketEndMs = formatters.shiftToSiteTimezone(
                    new Date(Date.UTC(y, m - 1, d + 1)).toISOString()
                  );
                }
              }

              // Build tooltip lines from overlapping events. For events
              // with outage_spans (inverter outages), match against the
              // individual spans rather than the parent event window.
              const tooltipLines: string[] = [];
              const esysFilteredEvents = eventLogEvents.filter(
                (e) => !selectedEsysUuid || !e.esysUuid || e.esysUuid === selectedEsysUuid
              );
              for (const event of esysFilteredEvents) {
                const color =
                  event.severity === "red" ? "#dc3545" : "#fd7e14";
                const dot = `<span style="display:inline-block;margin-right:4px;border-radius:2px;width:10px;height:10px;background-color:${color};opacity:0.5;"></span>`;

                if (
                  event.checkType === "inverter_outage_monthly" &&
                  event.details?.outage_spans &&
                  Array.isArray(event.details.outage_spans)
                ) {
                  // Resolve esys label for multi-esys sites
                  let esysPrefix = "";
                  if (
                    esysEntries &&
                    esysEntries.length > 1 &&
                    event.esysUuid
                  ) {
                    const esys = esysEntries.find(
                      ([, e]) => e.uuid === event.esysUuid
                    );
                    if (esys) {
                      const [, e] = esys;
                      const label = e.importGridConnectionId
                        ? e.importGridConnectionId.slice(-3)
                        : `${parseFloat(e.idx) + 1}`;
                      esysPrefix = `[${label}] `;
                    }
                  }

                  const spans = (
                    event.details.outage_spans as OutageSpan[]
                  ).filter((span) => {
                    const sMs = formatters.shiftToSiteTimezone(span.start);
                    const eMs = formatters.shiftToSiteTimezone(span.end);
                    return sMs < bucketEndMs && eMs > bucketStartMs;
                  });
                  for (const span of spans) {
                    tooltipLines.push(
                      `${dot}${esysPrefix}${span.serial_number} offline ${span.duration_min} min<br/>`
                    );
                  }
                } else {
                  // Standard events: match against parent event window
                  const evStartMs = formatters.shiftToSiteTimezone(
                    event.detectedAt
                  );
                  const evEndMs = event.resolvedAt
                    ? formatters.shiftToSiteTimezone(event.resolvedAt)
                    : Infinity;
                  if (evStartMs < bucketEndMs && evEndMs > bucketStartMs) {
                    const label = event.summary || event.notes || "Event";
                    tooltipLines.push(`${dot}${label}<br/>`);
                  }
                }
              }
              if (tooltipLines.length > 0) {
                tooltip += `<strong>Health events</strong><br/>`;
                tooltip += tooltipLines.join("");
              }
            }
          }

          return tooltip;
        },
      },
      legend: {
        show: false,
        data: legendRows.flat(),
        selected: legendSelected,
      },
      grid: {
        left: "60px",
        right: (() => {
          let r = 20;
          if (hasBattery) r += 20;
          if (periodsYAxisIndex !== null) r += 40;
          if (prYAxisIndex !== null) r += 40;
          return `${r}px`;
        })(),
        bottom: "60px",
        top: 20,
        containLabel: false,
      },
      xAxis: {
        type: "time",
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: [
        {
          type: "value",
          name: "Energy (kWh)",
        },
        ...(hasBattery
          ? [
              {
                type: "value",
                name: "Battery Charge (kWh)",
                nameTextStyle: { color: colors.batteryFinalCharge },
                position: "right",
                axisLine: { show: false },
                splitLine: { show: false },
                axisLabel: { color: colors.batteryFinalCharge },
              },
            ]
          : []),
        ...(periodsYAxisIndex !== null
          ? [
              {
                type: "value",
                name: "Periods",
                nameTextStyle: { color: "#888" },
                position: "right",
                offset: hasBattery ? 60 : 0,
                min: 0,
                max: aggregationMode === "none" ? 1.1 : undefined,
                axisLine: { show: false },
                splitLine: { show: false },
                axisLabel: { color: "#888" },
              },
            ]
          : []),
        ...(prYAxisIndex !== null
          ? [
              {
                type: "value",
                name: "PR (%)",
                nameTextStyle: { color: colors.performanceRatio },
                position: "right",
                offset:
                  (hasBattery ? 60 : 0) + (periodsYAxisIndex !== null ? 60 : 0),
                axisLine: { show: false },
                splitLine: { show: false },
                axisLabel: {
                  color: colors.performanceRatio,
                  formatter: "{value}%",
                },
              },
            ]
          : []),
      ],
      series: series,
    };

    // Compute Performance Ratio if weather actuals are available.
    // Returns both GPR (contract-ideal denominator, matches site health page)
    // and OPR (InRange model denominator) for the selected window so the
    // chart text below can render both lines.
    let performanceRatio: {
      gpr: number | null;
      opr: number | null;
    } | null = null;
    if (weatherActualsData && weatherActualsData.x.length > 0) {
      // Build a set of timestamps that have weather actuals coverage.
      // Use getTime (raw UTC epoch ms) instead of shiftToSiteTimezone so that
      // the coverage set and date range boundaries are in the same coordinate
      // system — shiftToSiteTimezone produces browser-TZ-dependent values that
      // don't compare correctly against dateRangeToTimestamps boundaries.
      const idealSummarySeries = waIdealSeries ?? waInrangeSeries ?? null;
      const inrangeSummarySeries = waInrangeSeries ?? null;
      const waCoverageSet = new Set<number>();
      const hasDateRange = startDate && endDate;
      let waIdealTotal = 0;
      let waInrangeTotal = 0;
      const accumulate = (i: number) => {
        if (idealSummarySeries) waIdealTotal += idealSummarySeries[i];
        if (inrangeSummarySeries) waInrangeTotal += inrangeSummarySeries[i];
      };
      if (hasDateRange) {
        const { startMs: waStartMs, endMs: waEndMs } =
          formatters.dateRangeToTimestamps(startDate, endDate);
        for (let i = 0; i < weatherActualsData.x.length; i++) {
          const ts = formatters.getTime(weatherActualsData.x[i]);
          if (ts >= waStartMs && ts <= waEndMs && !curtailmentSet.has(ts)) {
            accumulate(i);
            waCoverageSet.add(ts);
          }
        }
      } else {
        for (let i = 0; i < weatherActualsData.x.length; i++) {
          const ts = formatters.getTime(weatherActualsData.x[i]);
          if (!curtailmentSet.has(ts)) {
            accumulate(i);
            waCoverageSet.add(ts);
          }
        }
      }
      if (waIdealTotal > 0 || waInrangeTotal > 0) {
        // Only sum actual generation for periods with weather actuals data
        // and exclude curtailed periods from PR calculation.
        // Raw periods again: the coverage and curtailment sets are keyed on
        // settlement timestamps, so a bucketed point either contributes its
        // whole day on the strength of its first period's coverage or drops the
        // day entirely. Reading rawPoints keeps this figure identical at every
        // aggregation, which is what a whole-window ratio should be.
        let genWithCoverage = 0;
        for (const p of rawPoints) {
          if (!shouldIncludeSource(p.generationSource)) continue;
          const ts = formatters.getTime(p.x);
          if (waCoverageSet.has(ts) && !curtailmentSet.has(ts)) {
            genWithCoverage += p.generation;
          }
        }
        performanceRatio = {
          gpr: waIdealTotal > 0 ? genWithCoverage / waIdealTotal : null,
          opr: waInrangeTotal > 0 ? genWithCoverage / waInrangeTotal : null,
        };
      }
    }

    // Build color map for custom legend
    const legendColorMap: Record<string, string> = {};
    series.forEach((s: any) => {
      if (s.name && s.itemStyle?.color) {
        legendColorMap[s.name] = s.itemStyle.color;
      }
    });

    let availabilityPct: number | null = null;
    if (expectedPeriodCount > 0) {
      availabilityPct =
        Math.round(
          (1 - unavailablePeriodCount / expectedPeriodCount) * 100 * 1000
        ) / 1000;
      if (unavailablePeriodCount > 0 && availabilityPct === 100)
        availabilityPct = 99.999;
    }

    return {
      chartOptions,
      filteredData,
      performanceRatio,
      availabilityPct,
      legendRows,
      legendSelected,
      legendColorMap,
    };
  }, [
    data.data,
    data.unavailablePeriods,
    hasBattery,
    hasAnyZeroExport,
    isImportOnly,
    selectedEsysUuid,
    startDate,
    endDate,
    selectedSources,
    formatters,
    aggregationMode,
    forecastP50Data,
    forecastP90Data,
    weatherActualsData,
    curtailmentData,
    expectedGenerationPeriods,
    eventLogEvents,
    esysEntries,
    preset,
  ]);

  // Calculate maximum generation and solar demand (behindMeterPV) with their timestamps
  const maxStats = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return null;

    let maxGeneration = { value: 0, timestamp: "" };
    let maxSolarDemand = { value: 0, timestamp: "" };

    filteredData.forEach((point) => {
      if (point.generation > maxGeneration.value) {
        maxGeneration = { value: point.generation, timestamp: point.x };
      }
      if (point.behindMeterPV > maxSolarDemand.value) {
        maxSolarDemand = { value: point.behindMeterPV, timestamp: point.x };
      }
    });

    const formatTimeRange = (timestamp: string) => {
      const startTime = new Date(timestamp);
      const endTime = new Date(startTime.getTime() + 30 * 60 * 1000); // Add 30 minutes

      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: formatters.timeZone,
      };
      const timeWithTzOptions: Intl.DateTimeFormatOptions = {
        ...timeOptions,
        timeZoneName: "short",
      };
      const dateOptions: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: formatters.timeZone,
      };

      const startTimeWithTz = startTime.toLocaleTimeString(
        "en-GB",
        timeWithTzOptions
      );
      const endTimeWithTz = endTime.toLocaleTimeString(
        "en-GB",
        timeWithTzOptions
      );
      const dateStr = startTime.toLocaleDateString("en-GB", dateOptions);

      // Extract timezone from each time string
      const startTzMatch = startTimeWithTz.match(/\s([A-Z]{2,4})$/);
      const endTzMatch = endTimeWithTz.match(/\s([A-Z]{2,4})$/);
      const startTz = startTzMatch ? startTzMatch[1] : "";
      const endTz = endTzMatch ? endTzMatch[1] : "";

      const startTimeOnly = startTime.toLocaleTimeString("en-GB", timeOptions);
      const endTimeOnly = endTime.toLocaleTimeString("en-GB", timeOptions);

      // If timezones differ (DST transition), show both
      if (startTz !== endTz) {
        return `${startTimeOnly} ${startTz} and ${endTimeOnly} ${endTz} on ${dateStr}`;
      }

      // Otherwise show timezone once at the end
      return `${startTimeOnly} and ${endTimeOnly} ${startTz} on ${dateStr}`;
    };

    return {
      generation: {
        value: maxGeneration.value,
        timeRange: formatTimeRange(maxGeneration.timestamp),
      },
      solarDemand: {
        value: maxSolarDemand.value,
        timeRange: formatTimeRange(maxSolarDemand.timestamp),
      },
    };
  }, [filteredData, formatters.timeZone]);

  if (!chartOptions || Object.keys(chartOptions).length === 0) {
    return (
      <Row>
        <Col>
          <div className="alert alert-warning" role="alert">
            No data available for the selected date range.
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="mt-3">
      <Col>
        <h5>
          Processed Readings Chart{siteName ? ` - ${siteName}` : ""}
          {forecastLoading ? (
            <Spinner animation="border" size="sm" className="ms-2 text-muted" />
          ) : (
            ""
          )}
        </h5>
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "8px",
            fontSize: "0.8rem",
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: "#666" }}>Presets:</span>
          {(
            [
              { key: "default" as PresetName, label: "Default" },
              {
                key: "gen-vs-forecast" as PresetName,
                label: "Generation vs Forecast",
              },
              {
                key: "demand-vs-forecast" as PresetName,
                label: "Demand vs Forecast",
              },
              { key: "availability" as PresetName, label: "PV Availability" },
              {
                key: "performance-ratio" as PresetName,
                label: "Performance Ratio",
              },
            ] as const
          ).map(({ key, label }) => (
            <span
              key={key}
              onClick={() => handlePresetClick(key)}
              style={{
                cursor: "pointer",
                fontWeight: preset === key ? 700 : 400,
                color: preset === key ? "#333" : "#666",
                textDecoration: preset === key ? "none" : "underline",
                userSelect: "none",
              }}
            >
              {label}
            </span>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            marginBottom: "16px",
          }}
        >
          {(legendRows ?? []).map((row, ri) => (
            <div
              key={ri}
              style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}
            >
              {row.map((name) => {
                const isVisible =
                  name in visibleSeries
                    ? visibleSeries[name]
                    : ((legendSelected ?? {})[name] ?? true);
                const color = (legendColorMap ?? {})[name] ?? "#888";
                return (
                  <span
                    key={name}
                    onClick={() => handleCustomLegendClick(name)}
                    style={{
                      cursor: "pointer",
                      opacity: isVisible ? 1 : 0.35,
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                      userSelect: "none",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "14px",
                        height: "3px",
                        backgroundColor: color,
                        marginRight: "4px",
                        verticalAlign: "middle",
                        borderRadius: "1px",
                      }}
                    />
                    {name}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
        <ReactECharts
          ref={chartRef}
          key={`${selectedEsysUuid}`}
          notMerge={true}
          option={chartOptions}
          style={{ height: "400px" }}
          onEvents={{ legendselectchanged: handleLegendChange }}
        />
        {maxStats && !isImportOnly && aggregationMode === "none" && (
          <div className="mt-3">
            <p className="mb-1">
              Half-hour maximum generated energy (
              {maxStats.generation.value.toFixed(3)} kWh) between{" "}
              {maxStats.generation.timeRange}.
            </p>
            <p className="mb-1">
              Half-hour maximum solar demand (
              {maxStats.solarDemand.value.toFixed(3)} kWh) between{" "}
              {maxStats.solarDemand.timeRange}.
            </p>
          </div>
        )}
        {(availabilityPct != null || performanceRatio) && !isImportOnly && (
          <div className="mt-3">
            {availabilityPct != null && (
              <p className="mb-1">
                PV System Availability:{" "}
                <strong>{availabilityPct.toFixed(3)}%</strong>
              </p>
            )}
            {performanceRatio && performanceRatio.gpr !== null && (
              <p className="mb-1">
                GPR (Meteonorm):{" "}
                <strong>{(performanceRatio.gpr * 100).toFixed(1)}%</strong>
              </p>
            )}
            {performanceRatio && performanceRatio.opr !== null && (
              <p className="mb-1">
                OPR (Meteonorm):{" "}
                <strong>{(performanceRatio.opr * 100).toFixed(1)}%</strong>
              </p>
            )}
          </div>
        )}
      </Col>
    </Row>
  );
};

export default ChartProcessed;
